import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { PropsWithChildren, useCallback, useState } from 'react';
import { Source, ViewState } from 'react-map-gl';
import { useDeepCompareMemo } from 'use-deep-compare';
import { getBoundsFromFeatureCollection } from '../../map/utils/getBoundsFromFeatureCollection';
import { BloomfieldMap } from '../BloomfieldMap';
import { PointColorLegend } from './controls/PointColorLegend';
import { usePointHeatmap } from './hooks/usePointHeatmap';
import { ClusterPointsLayer } from './layers/ClusterPointsLayer';
import {
  BLOCKS_BOUNDARY_SOURCE_ID,
  POINTS_SOURCE_ID,
  SINGLE_POINT_LAYER_ID,
} from './layers/constants';
import { OutlinedBlocksLayer } from './layers/OutlinedBlocksLayer';
import { SelectedPointLayer } from './layers/SelectedPointLayer';
import { SinglePointLayer } from './layers/SinglePointLayer';
import { Point, PointColorConfiguration } from './types';

type Props<P extends Point> = PropsWithChildren<{
  pointsFeatureCollection: ReturnType<
    typeof usePointHeatmap<P>
  >['pointsFeatureCollection'];
  blocksFeatureCollection: ReturnType<
    typeof usePointHeatmap<P>
  >['blocksFeatureCollection'];
  pointColorConfig: PointColorConfiguration<P>;
  /**
   * Optional extra layers to be added to the map. Note, these layers
   * live within the `points` source context.
   */
  extraPointLayers?: React.ReactNode;
  onMapClick?: (event: mapboxgl.MapLayerMouseEvent) => void;
  selectedPointId?: string;
  hideColorLegend?: boolean;
}>;

export const PointHeatmap = <P extends Point = Point>({
  pointsFeatureCollection,
  blocksFeatureCollection,
  children,
  pointColorConfig,
  extraPointLayers,
  onMapClick,
  selectedPointId,
  hideColorLegend,
}: Props<P>) => {
  // Set initial view state bounds to the bounds of the blocks
  const initialViewState: Partial<ViewState> & {
    bounds?: mapboxgl.LngLatBoundsLike;
    fitBoundsOptions?: mapboxgl.FitBoundsOptions | undefined;
  } = useDeepCompareMemo(
    () => ({
      bounds:
        getBoundsFromFeatureCollection(blocksFeatureCollection) || undefined,
    }),
    [blocksFeatureCollection]
  );

  /** Logic for displaying a pointer cursor when hovering a point */
  const [cursor, setCursor] = useState<string>('auto');
  const onMouseEnter = useCallback(() => setCursor('pointer'), []);
  const onMouseLeave = useCallback(() => setCursor('auto'), []);

  return (
    <BloomfieldMap
      style={{
        width: '100%',
        height: '100%',
      }}
      initialViewState={initialViewState}
      interactiveLayerIds={[SINGLE_POINT_LAYER_ID]}
      onClick={onMapClick}
      cursor={cursor}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Source
        id={BLOCKS_BOUNDARY_SOURCE_ID}
        type='geojson'
        data={blocksFeatureCollection}
      >
        <OutlinedBlocksLayer source={BLOCKS_BOUNDARY_SOURCE_ID} />
      </Source>
      <Source
        id={POINTS_SOURCE_ID}
        type='geojson'
        data={pointsFeatureCollection}
        cluster={true}
        clusterMaxZoom={16}
        clusterRadius={25}
      >
        <ClusterPointsLayer circleColor={pointColorConfig.color} />
        <SinglePointLayer />
        <SelectedPointLayer selectedPointId={selectedPointId} />
        {extraPointLayers}
      </Source>

      {/* Map Controls */}
      {children}
      {!hideColorLegend && <PointColorLegend colorConfig={pointColorConfig} />}
    </BloomfieldMap>
  );
};
